import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const FloatingLanguageSwitcher: React.FC = () => {
  const { currentLanguage, changeLanguage } = useLanguage();

  // WhatsApp Link Component
  const WhatsAppLink: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => {
    // WhatsApp numbers based on language
    const whatsappNumbers = {
      en: '+610401461019', // Tony
      id: '+6281337329299' // Nila
    };

    // Default messages based on language
    const defaultMessages = {
      en: 'Hello Tony! I would like to enquire about ordering a custom cake from TN Cakes Bali®',
      id: 'Halo Nila! Saya ingin bertanya tentang pemesanan kue custom dari TN Cakes Bali®'
    };

    const phoneNumber = whatsappNumbers[currentLanguage as keyof typeof whatsappNumbers] || whatsappNumbers.en;
    const message = defaultMessages[currentLanguage as keyof typeof defaultMessages] || defaultMessages.en;
    const encodedMessage = encodeURIComponent(message);

    const handleClick = () => {
      // Track WhatsApp click
      if (window.gtag) {
        window.gtag('event', 'whatsapp_click', {
          event_category: 'Contact',
          event_label: `WhatsApp ${currentLanguage.toUpperCase()}`,
        });
      }
    };

    return (
      <a
        href={`https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodedMessage}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className={className}
      >
        {children}
      </a>
    );
  };

  return (
    <>
      {/* Desktop: Bottom Right Corner */}
      <div className="hidden md:block fixed bottom-6 right-6 z-50">
        <div className="bg-white/90 backdrop-blur-md border border-white/20 shadow-lg rounded-full p-1">
          <div className="flex items-center">
            <button
              onClick={() => currentLanguage !== 'en' && changeLanguage('en')}
              aria-label="Switch to English"
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                currentLanguage === 'en'
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <span className="text-sm font-semibold">English</span>
            </button>
            <button
              onClick={() => currentLanguage !== 'id' && changeLanguage('id')}
              aria-label="Switch to Indonesian"
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                currentLanguage === 'id'
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <span className="text-sm font-semibold">Bahasa</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile: Above Navigation Menu */}
      <div className="md:hidden fixed bottom-16 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3">
        {/* Language Switcher */}
        <div className="bg-white/90 backdrop-blur-md border border-white/20 shadow-lg rounded-full p-1">
          <div className="flex items-center">
            <button
              onClick={() => currentLanguage !== 'en' && changeLanguage('en')}
              aria-label="Switch to English"
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                currentLanguage === 'en'
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <span className="text-sm font-semibold">English</span>
            </button>
            <button
              onClick={() => currentLanguage !== 'id' && changeLanguage('id')}
              aria-label="Switch to Indonesian"
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                currentLanguage === 'id'
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <span className="text-sm font-semibold">Bahasa</span>
            </button>
          </div>
        </div>

        {/* Floating Action Buttons */}
        <div className="flex items-center gap-2">
          <Link
            to="/inquiry"
            className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-red-400 to-pink-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
            aria-label="Shop Now"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </Link>
          <WhatsAppLink
            className="flex items-center justify-center w-12 h-12 bg-green-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
            aria-label="WhatsApp Us"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
          </WhatsAppLink>
        </div>
      </div>
    </>
  );
};

export default FloatingLanguageSwitcher;
